// components/BlogDashboard.jsx
import React, { useState, useEffect } from 'react';
import blogService from '../services/blogServices';

const BlogWrite = () => {
  const [blogs, setBlogs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    content: '',
    publishDate: new Date().toISOString().split('T')[0],
    image: null,
    imagePreview: '',
    status: 'draft'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    'Education', 'Institutions'
  ];

  useEffect(() => {
    // Check admin status
    const checkAdminStatus = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const isUserAdmin = user.email === 'neoking12@proton.me' || localStorage.getItem('isAdmin') === 'true';
        setIsAdmin(isUserAdmin);
        console.log('👑 Admin status:', isUserAdmin);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();

    // Load blogs from storage immediately
    const loadInitialBlogs = () => {
      console.log('🔄 Loading initial articles...');
      const allBlogs = blogService.getAllBlogs();
      console.log('📊 Initial articles loaded:', allBlogs.length);
      setBlogs(allBlogs);
      setStats(blogService.getBlogStats());
      setIsLoading(false);
    };

    loadInitialBlogs();

    // Subscribe to real-time updates
    const unsubscribe = blogService.addListener((data) => {
      console.log('🔔 Real-time update received:', data);
      if (data.allBlogs) {
        setBlogs(data.allBlogs);
        console.log('📊 Updated articless count:', data.allBlogs.length);
      }
      if (data.stats) {
        setStats(data.stats);
      }
    });

    // Listen for storage events (when localStorage changes in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'blog_posts') {
        console.log('🔄 Storage changed, reloading articless...');
        loadInitialBlogs();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      if (unsubscribe) unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const loadBlogs = () => {
    console.log('🔄 Manually loading articles...');
    const allBlogs = blogService.getAllBlogs();
    console.log('📊 Loaded article count:', allBlogs.length);
    setBlogs(allBlogs);
    setStats(blogService.getBlogStats());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
        return;
      }
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please upload an image file' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Please enter a title' });
      return false;
    }
    if (!formData.category) {
      setMessage({ type: 'error', text: 'Please select a category' });
      return false;
    }
    if (!formData.content.trim()) {
      setMessage({ type: 'error', text: 'Please enter article content' });
      return false;
    }
    if (formData.content.trim().length < 50) {
      setMessage({ type: 'error', text: 'Article content should be at least 50 characters' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserEmail = user.email || 'admin@example.com';
      
      let imageUrl = formData.imagePreview;
      if (!imageUrl) {
        imageUrl = `https://via.placeholder.com/800x400?text=${encodeURIComponent(formData.title.substring(0, 20))}`;
      }

      const blogData = {
        id: editingBlog ? editingBlog.id : null,
        title: formData.title.trim(),
        category: formData.category,
        content: formData.content.trim(),
        publishDate: formData.publishDate,
        image: imageUrl,
        status: formData.status,
        author: currentUserEmail,
      };

      const result = await blogService.saveBlog(blogData);
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: editingBlog ? 'Article updated successfully!' : 
                formData.status === 'published' ? 'Article published successfully!' : 'Article saved as draft!'
        });
        
        // Immediately refresh the blogs list
        setTimeout(() => {
          loadBlogs();
        }, 100);
        
        resetForm();
        
        setTimeout(() => {
          setShowForm(false);
          setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        }, 2000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      setMessage({ type: 'error', text: 'Error saving blog. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      content: '',
      publishDate: new Date().toISOString().split('T')[0],
      image: null,
      imagePreview: '',
      status: 'draft'
    });
    setEditingBlog(null);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      category: blog.category,
      content: blog.content,
      publishDate: blog.publishDate,
      image: null,
      imagePreview: blog.image,
      status: blog.status
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      const result = blogService.deleteBlog(blogId);
      if (result.success) {
        setMessage({ type: 'success', text: 'Blog deleted successfully!' });
        loadBlogs(); // Refresh the list
        setTimeout(() => setMessage({ type: '', text: '' }), 2000);
      } else {
        setMessage({ type: 'error', text: 'Error deleting blog. Please try again.' });
      }
    }
  };

  const handlePublish = (blogId) => {
    const result = blogService.publishBlog(blogId);
    if (result.success) {
      setMessage({ type: 'success', text: 'Blog published successfully!' });
      loadBlogs(); // Refresh the list
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } else {
      setMessage({ type: 'error', text: 'Error publishing blog. Please try again.' });
    }
  };

  const handleUnpublish = (blogId) => {
    const result = blogService.saveAsDraft(blogId);
    if (result.success) {
      setMessage({ type: 'success', text: 'Blog unpublished and saved as draft!' });
      loadBlogs(); // Refresh the list
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } else {
      setMessage({ type: 'error', text: 'Error unpublishing blog. Please try again.' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gold to-blue-500 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-fourth text-white shadow-lg">
        <div className="container mx-auto px-4 pt-48 pb-10">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary">Article Management Dashboard</h1>
              <p className="mt-2 opacity-90">Manage and publish your articles</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="bg-white text-purple-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {showForm ? 'Cancel' : '+ New Article'}
            </button>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white animate-slide-in max-w-md`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            )}
            {message.text}
          </div>
        </div>
      )}

      <div className="container mx-auto px-2 py-8">
        {showForm && (
          <div className="bg-white rounded-lg shadow-xl px-2 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingBlog ? 'Edit Article' : 'Create New Article'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Article Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter article title"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Publishing Date *</label>
                  <input
                    type="date"
                    name="publishDate"
                    value={formData.publishDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Featured Image</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300">
                    <span className="text-gray-600">Choose Image</span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                  {formData.imagePreview && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, imagePreview: '', image: null }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {formData.imagePreview && (
                  <div className="mt-4">
                    <img src={formData.imagePreview} alt="Preview" className="h-48 w-full object-cover rounded-lg" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Article Content * (Minimum 50 characters)</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows="12"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Write your content here..."
                  required
                />
                {formData.content && formData.content.length < 50 && (
                  <p className="text-red-500 text-sm mt-1">
                    {50 - formData.content.length} more characters needed (minimum 50)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Status</label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="status" 
                      value="draft" 
                      checked={formData.status === 'draft'} 
                      onChange={handleInputChange} 
                      className="mr-2" 
                    />
                    Draft (Save as draft)
                  </label>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="status" 
                      value="published" 
                      checked={formData.status === 'published'} 
                      onChange={handleInputChange} 
                      className="mr-2" 
                    />
                    Publish immediately
                  </label>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="bg-third px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  {isSubmitting ? 'Saving...' : editingBlog ? 'Update Article' : formData.status === 'published' ? 'Publish Article' : 'Save as Draft'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { resetForm(); setShowForm(false); }} 
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-hidden">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <div>
              <h2 className="text-md font-bold text-gray-800">All Articles</h2>
              <p className="text-gray-600 text-sm">Total: {blogs.length} posts (Published: {stats.published}, Drafts: {stats.drafts})</p>
            </div>
          </div>

          {blogs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No articles yet. Click "New Article" to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {blogs.map((blog) => (
                    <tr key={blog.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <img src={blog.image} alt={blog.title} className="h-12 w-20 object-cover rounded" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">{blog.title}</div>
                        <div className="text-xs text-gray-500">by {blog.author}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-600 rounded-full font-semibold">
                          {blog.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">{blog.publishDate}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                          blog.status === 'published' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {blog.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-3">
                          <button 
                            onClick={() => handleEdit(blog)} 
                            className="text-blue-600 hover:text-blue-800 sm:text-sm text-ss font-medium transition-colors"
                          >
                            Edit
                          </button>
                          {blog.status === 'draft' ? (
                            <button 
                              onClick={() => handlePublish(blog.id)} 
                              className="text-green-600 hover:text-green-800 sm:text-sm text-ss font-medium transition-colors"
                            >
                              Publish
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleUnpublish(blog.id)} 
                              className="text-orange-600 hover:text-orange-800 sm:text-sm text-ss font-medium transition-colors"
                            >
                              Unpublish
                            </button>
                          )}
                          <button 
                            onClick={() => handleDelete(blog.id)} 
                            className="text-red-600 hover:text-red-800 sm:text-sm text-ss font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogWrite;